import { rem } from 'polished'
import styled from 'styled-components'

import RoomDetails from '../Common/RoomDetails'

import FoldableContent from 'components/Common/FoldableContent/FoldableContent'
import { BaseImage } from 'components/Common/OrderView/styles'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useIntl } from 'react-intl'

interface Prop {
  offer: App.Offer
  order: App.Order
}

const ReservedFor = styled.div`
  display: flex;
  flex-direction: column;
`

const BookingDetails = styled.div`
  padding: ${rem(20)} 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

function LeOfferPackageSummary({ offer, order }: Prop) {
  const intl = useIntl()

  const yourPackageHeading = intl.formatMessage({
    id: 'ATQ2hUaqRXa1I_JNESNo',
    defaultMessage: 'Your Package',
    description: 'Heading for the accommodation package summary section',
  })

  const hideDetailsLabel = intl.formatMessage({
    id: 'WROTB4yfWNTtJqEHuisy',
    defaultMessage: 'Hide package and room details',
    description: 'Button text to collapse the package and room details section',
  })

  const viewDetailsLabel = intl.formatMessage({
    id: 'hshw9v-0r9nl57Ju-BiU',
    defaultMessage: 'View package and room details',
    description: 'Button text to expand the package and room details section',
  })

  const pkg = (orderItem: App.OrderItem) =>
    offer &&
    orderItem &&
    offer?.packages &&
    offer.packages.length > 0 &&
    offer.packages.find((p) => p.uniqueKey === orderItem.package.uniqueKey)

  const getImage = (orderPackage: App.Package) => {
    if (
      orderPackage?.roomType?.images &&
      orderPackage.roomType.images.length > 0
    ) {
      return orderPackage.roomType.images[0].id
    }
    if (offer?.image?.id) {
      return offer.image.id
    }
  }

  return (
    <>
      {order?.items?.map((orderItem) => {
        const orderPackage = pkg(orderItem)
        const imageId = orderPackage && getImage(orderPackage)

        return (
          orderItem?.reservation && (
            <BookingDetails key={orderItem.id}>
              <Heading variant="heading5">{yourPackageHeading}</Heading>
              {imageId && (
                <BaseImage
                  className="hidden-print"
                  id={imageId}
                  mobileAspectRatio="9:4"
                  mobileWidth="100"
                />
              )}
              {orderItem.package.name ||
                (orderItem?.reservation.roomType && (
                  <div>
                    {orderItem.package.name && (
                      <BodyText variant="medium" weight="bold">
                        {orderItem.package.name}
                      </BodyText>
                    )}
                    {orderItem?.reservation.roomType && (
                      <BodyText variant="medium">
                        {orderItem?.reservation.roomType.name}
                      </BodyText>
                    )}
                  </div>
                ))}
              {orderItem?.reservation && (
                <ReservedFor>
                  <BodyText variant="large">
                    {intl.formatMessage(
                      {
                        id: 'fCcFtHfbHwv2asjtjIA1',
                        defaultMessage: '<bold>Reserved for</bold> {guestFirstName} {guestLastName}, {adultCountLabel}',
                        description: 'Guest name and adult count for accommodation reservation summary',
                      },
                      {
                        guestFirstName: orderItem?.reservation.guestFirstName,
                        guestLastName: orderItem?.reservation.guestLastName,
                        adultCountLabel: intl.formatMessage(
                          {
                            id: 'Oytf0w6JBrDb5yoe5inc',
                            defaultMessage: '{adultCount, plural, one {# adult} other {# adults}}',
                            description: 'Adult count label shown in trip accommodation reservation summary',
                          },
                          { adultCount: orderItem?.reservation.adults },
                        ),
                        bold: (chunks) => <BodyText as="span" variant="medium" weight="bold">{chunks}</BodyText>,
                      },
                    )}
                  </BodyText>
                </ReservedFor>
              )}
              {orderPackage && (
                <FoldableContent
                  openLabel={hideDetailsLabel}
                  closedLabel={viewDetailsLabel}
                >
                  <RoomDetails pkg={orderPackage} />
                </FoldableContent>
              )}
            </BookingDetails>
          )
        )
      })}
    </>
  )
}

export default LeOfferPackageSummary
